import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import PublicHeader from "@/components/layout/PublicHeader";
import { toast } from "sonner";
import { ArrowRight, Briefcase } from "lucide-react";
import { useSEO } from "@/lib/useSEO";

export default function RecruiterRegister() {
  const { setUser } = useAuth();
  const navigate = useNavigate();
  useSEO({
    title: "Recruiter Sign-up — Maqam",
    description: "Post jobs and reach qualified Saudi candidates on Maqam. AI-matched applications delivered straight to your inbox.",
    path: "/recruiter/register",
  });
  const [form, setForm] = useState({
    email: "",
    password: "",
    full_name: "",
    company_name: "",
    company_size: "11-50",
  });
  const [loading, setLoading] = useState(false);

  const change = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const r = await api.post("/recruiter/register", form);
      localStorage.setItem("jf_token", r.data.token);
      setUser(r.data.user);
      toast.success(`Welcome ${r.data.user.full_name}`);
      navigate("/recruiter/search");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Registration failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-white text-black">
      <PublicHeader />
      <section className="max-w-2xl mx-auto px-6 md:px-10 py-16">
        <div className="mb-10">
          <div className="inline-flex items-center gap-2 border border-black/20 px-4 py-2 mb-6 font-mono text-xs uppercase tracking-widest">
            <Briefcase className="w-3.5 h-3.5" /> For Employers
          </div>
          <h1 className="font-display font-black text-5xl tracking-tighter leading-[0.95] mb-4">
            Hire pre-verified talent <br />
            <span className="text-gradient">across the Kingdom.</span>
          </h1>
          <p className="text-gray-700">
            Search thousands of candidates filtered by skill badges (Bronze → Platinum), Nitaqat tier, city, and
            experience. Each badge is proctored with anti-cheat integrity scoring.
          </p>
        </div>

        <form onSubmit={submit} className="space-y-5 border border-black/10 p-6" data-testid="recruiter-register-form">
          <div className="grid md:grid-cols-2 gap-5">
            <label>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Your name</span>
              <input required value={form.full_name} onChange={change("full_name")} className="w-full border-b-2 border-gray-200 focus:border-black outline-none py-3" data-testid="rr-name" />
            </label>
            <label>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Company name</span>
              <input required value={form.company_name} onChange={change("company_name")} className="w-full border-b-2 border-gray-200 focus:border-black outline-none py-3" data-testid="rr-company" />
            </label>
            <label>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Work email</span>
              <input type="email" required value={form.email} onChange={change("email")} className="w-full border-b-2 border-gray-200 focus:border-black outline-none py-3" data-testid="rr-email" />
            </label>
            <label>
              <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Password</span>
              <input type="password" required minLength={10} pattern="(?=.*[A-Z])(?=.*[a-z])(?=.*\d)(?=.*[\W_]).{10,}" title="At least 10 characters with uppercase, lowercase, number and special character." value={form.password} onChange={change("password")} className="w-full border-b-2 border-gray-200 focus:border-black outline-none py-3" data-testid="rr-password" />
            </label>
            <label className="md:col-span-2">
              <span className="block font-mono text-[10px] uppercase tracking-widest text-gray-500 mb-2">Company size</span>
              <select value={form.company_size} onChange={change("company_size")} className="w-full border-b-2 border-gray-200 focus:border-black outline-none py-3 bg-transparent">
                {["1-10", "11-50", "51-200", "201-1000", "1000+"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </label>
          </div>
          <button disabled={loading} className="w-full bg-black text-white hover:bg-[#0033FF] font-bold py-5 flex items-center justify-center gap-3 transition-colors disabled:opacity-60" data-testid="rr-submit">
            {loading ? "…" : "Create recruiter account"} <ArrowRight className="w-5 h-5" />
          </button>
          <div className="text-sm text-center text-gray-600">
            Already a recruiter? <Link to="/login" className="link-underline text-[#0033FF] font-medium">Login</Link>
          </div>
        </form>
      </section>
    </div>
  );
}
